import { formatCurrency, formatDate, formatPercent } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import { AlgoMode, Currency } from "@front/m19-api-client";
import { CurrencySymbols, StrategyEx } from "@front/m19-models";
import { IButtonDirective, IIconComponent, ITooltipDirective, ModalService } from "@front/m19-ui";
import { TranslocoService } from "@jsverse/transloco";
import {
  SwitchTargetAlgoModalComponent,
  TargetAlgoModeSelectionData,
} from "@m19-board/insights/advertising-stats/switch-target-algo-modal/switch-target-algo-modal.component";

@Component({
  selector: "app-algo-target-renderer-v2",
  imports: [IButtonDirective, ITooltipDirective, IIconComponent],
  template: `
    <div class="group/algo-target flex items-center gap-0.5">
      @if (targetFormatted()) {
        @if (addLabel()) {
          <div class="text-gray-500">{{ label() }}</div>
        }
        <div>
          <button
            iButton
            variant="ghost"
            size="sm"
            [disabled]="isReadonly()"
            [iTooltip]="tooltip()"
            (click)="openTargetModal(); $event.stopPropagation()"
          >
            {{ targetFormatted() }}
            <IIcon name="edit" class="opacity-0 group-hover/algo-target:opacity-100" />
          </button>
        </div>
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AlgoTargetButtonComponent {
  private modalService = inject(ModalService);
  private translocoService = inject(TranslocoService);
  strategy = input.required<StrategyEx>();

  organizationId = input.required<number>();
  locale = input.required<string>();
  currency = input.required<Currency>();
  isReadonly = input.required<boolean>();
  addLabel = input<boolean>(false);

  tooltip = computed(() => {
    switch (this.strategy().algoMode) {
      case AlgoMode.ACOS_TARGET:
        if (this.strategy().dailyBudget) {
          return this.translocoService.translate("algo-target-renderer.acos_target_with_daily_budget");
        }
        return this.translocoService.translate("algo-target-renderer.acos_target");
      case AlgoMode.PRODUCT_LAUNCH:
        return this.translocoService.translate("algo-target-renderer.suggested_bid");
      case AlgoMode.MONTHLY_BUDGET_TARGET:
        return this.translocoService.translate("metrics.MONTHLY_BUDGET_tooltip");
      case AlgoMode.TACOS_TARGET:
        return this.translocoService.translate("algo-target-renderer.tacos_target");
      case AlgoMode.AMAZON_MANAGED_BUDGET: {
        const budgetFlight = this.strategy().budgetFlight;
        if (budgetFlight) {
          const budget = formatCurrency(
            budgetFlight.budget!,
            this.locale(),
            CurrencySymbols[this.currency()],
            this.currency(),
            "1.0-0",
          );
          const startDate = formatDate(budgetFlight.startDate!, "shortDate", this.locale());
          const endDate = formatDate(budgetFlight.endDate!, "shortDate", this.locale());
          return this.translocoService.translate("algo-target-renderer.amazon_managed_budget_with_dates", {
            budget,
            startDate,
            endDate,
          });
        }
        return this.translocoService.translate("algo-target-renderer.click_to_setup_new_budget");
      }
    }
    return "";
  });

  label = computed(() => {
    if (this.strategy().algoMode) {
      if (this.strategy().algoMode === AlgoMode.ACOS_TARGET) {
        return this.translocoService.translate("algo-target-renderer.acos_target");
      } else if (this.strategy().algoMode === AlgoMode.PRODUCT_LAUNCH) {
        return this.translocoService.translate("algo-target-renderer.bid");
      } else if (this.strategy().algoMode === AlgoMode.MONTHLY_BUDGET_TARGET) {
        if (this.strategy().nextMonthlyBudget) {
          return this.translocoService.translate("switch-target-algo-modal.monhly_next_monthly_budget");
        }
        return this.translocoService.translate("metrics.MONTHLY_BUDGET_tooltip");
      } else if (this.strategy().algoMode === AlgoMode.TACOS_TARGET) {
        return this.translocoService.translate("algo-target-renderer.tacos_target");
      } else if (this.strategy().algoMode === AlgoMode.AMAZON_MANAGED_BUDGET) {
        return this.translocoService.translate("algo-target-renderer.amazon_managed_budget");
      }
    }
    return "";
  });

  targetFormatted = computed<string>(() => this.getFormattedTarget(this.strategy()));

  private getFormattedTarget(strategy: StrategyEx): string {
    if (strategy.algoMode === AlgoMode.ACOS_TARGET) {
      const target = formatPercent(strategy.acosTarget!, this.locale(), "1.0-0");
      const secTarget = `${
        strategy.dailyBudget
          ? formatCurrency(
              strategy.dailyBudget,
              this.locale()!,
              CurrencySymbols[this.currency()],
              this.currency(),
              "1.0-0",
            )
          : ""
      }`;
      return target + (secTarget ? ` / ${secTarget}` : "");
    } else if (strategy.algoMode === AlgoMode.TACOS_TARGET && strategy.isSpStrategy()) {
      return formatPercent(strategy.tacosTarget!, this.locale(), "1.0-0");
    } else if (strategy.algoMode === AlgoMode.PRODUCT_LAUNCH) {
      const target = `${formatCurrency(
        strategy.suggestedBid!,
        this.locale(),
        CurrencySymbols[this.currency()],
        this.currency(),
        "1.0-2",
      )}`;
      const secTarget = `${formatCurrency(
        strategy.dailyBudget!,
        this.locale(),
        CurrencySymbols[this.currency()],
        this.currency(),
        "1.0-0",
      )}`;
      return target + (secTarget ? ` / ${secTarget}` : "");
    } else if (strategy.algoMode === AlgoMode.MONTHLY_BUDGET_TARGET) {
      let target = formatCurrency(
        strategy.monthlyBudget!,
        this.locale(),
        CurrencySymbols[this.currency()],
        this.currency(),
        "1.0-0",
      );
      if (strategy.nextMonthlyBudget) {
        target += ` / ${formatCurrency(
          strategy.nextMonthlyBudget!,
          this.locale(),
          CurrencySymbols[this.currency()],
          this.currency(),
          "1.0-0",
        )}`;
      }
      return target;
    } else if (strategy.algoMode === AlgoMode.AMAZON_MANAGED_BUDGET) {
      if (strategy.budgetFlight) {
        const budget = formatCurrency(
          strategy.budgetFlight.budget!,
          this.locale(),
          CurrencySymbols[this.currency()],
          this.currency(),
          "1.0-0",
        );
        const startDate = formatDate(strategy.budgetFlight.startDate!, "shortDate", this.locale());
        const endDate = formatDate(strategy.budgetFlight.endDate!, "shortDate", this.locale());
        return `${budget} (${startDate} - ${endDate})`;
      }
      return this.translocoService.translate("common.ended");
    }
    return "";
  }

  openTargetModal(): void {
    this.modalService.openModal<TargetAlgoModeSelectionData, void>(SwitchTargetAlgoModalComponent, {
      modalTitle: this.translocoService.translate("algo-target-renderer.change_algorithm_target"),
      data: {
        strategy: this.strategy(),
        organizationId: this.organizationId(),
        currencyCode: this.currency(),
      },
    });
  }
}
